/**
 * FinanceApp — Onboarding Wizard (4 étapes : Société → Facturation → Client → Lancement)
 * Affiché uniquement pour les nouveaux utilisateurs (onboarding_completed = 0 ou null),
 * après le questionnaire activité/priorités (ActivityQuestionnaire.jsx).
 */
import { useState, useEffect } from "react";
import api from "../../services/api";

// ─── Données statiques ─────────────────────────────────────────────────────────
const LEGAL_FORMS = ["SAS", "SASU", "SARL", "EURL", "SA", "SCI", "EI", "Micro-entreprise", "Association", "Autre"];

const CURRENCIES  = [
  { value: "EUR", label: "EUR — Euro (€)" },
  { value: "USD", label: "USD — Dollar américain ($)" },
  { value: "GBP", label: "GBP — Livre sterling (£)" },
  { value: "CHF", label: "CHF — Franc suisse (CHF)" },
  { value: "CAD", label: "CAD — Dollar canadien (CA$)" },
];

const LANGS = [
  { value: "fr", label: "🇫🇷 Français" },
  { value: "en", label: "🇬🇧 English" },
  { value: "es", label: "🇪🇸 Español" },
  { value: "de", label: "🇩🇪 Deutsch" },
  { value: "it", label: "🇮🇹 Italiano" },
  { value: "ar", label: "🇸🇦 العربية" },
];

const PAYMENT_TERMS = [
  "Comptant à la réception",
  "15 jours nets",
  "30 jours nets",
  "45 jours nets",
  "60 jours nets",
  "Fin de mois + 30 jours",
];

// Étape Banque retirée le 09/09/2026 — le bouton "Connecter via Bridge"
// était un placeholder mort (window.open("#","_blank"), jamais implémenté),
// un abonné ne doit jamais rencontrer un bouton cassé dans son tout premier
// parcours. Backend (POST /onboarding/bank) conservé intact pour
// réintroduction future si l'intégration Bridge est un jour activée.
const STEPS = [
  { id: 1, label: "Société",    icon: "🏢" },
  { id: 2, label: "Facturation", icon: "🧾" },
  { id: 3, label: "Client",     icon: "👥" },
  { id: 4, label: "Lancement",  icon: "🎉" },
];

// ─── Validations ───────────────────────────────────────────────────────────────
const isSirenValid  = (v) => !v || /^\d{9}$/.test(v.replace(/\s/g, ""));
const isEmailValid  = (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const isIbanValid   = (v) => !v || /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(v.replace(/\s/g, ""));

// ─── Styles inline partagés ────────────────────────────────────────────────────
const S = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(17,24,39,0.55)",
    zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center",
    padding: "16px", overflowY: "auto",
  },
  card: {
    background: "#ffffff", borderRadius: 24, width: "100%", maxWidth: 680,
    boxShadow: "0 25px 80px rgba(0,0,0,0.18)",
    display: "flex", flexDirection: "column", margin: "auto",
  },
  header: { padding: "32px 40px 24px" },
  body:   { padding: "0 40px", flex: 1 },
  footer: { padding: "24px 40px 32px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #f3f4f6" },
  label:  { display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5, letterSpacing: "0.02em" },
  input: {
    width: "100%", padding: "10px 12px", border: "1px solid #d1d5db",
    borderRadius: 8, fontSize: 13, color: "#111827", background: "#ffffff",
    boxSizing: "border-box", outline: "none", transition: "border-color 0.15s",
  },
  inputErr: { borderColor: "#f87171" },
  inputOk:  { borderColor: "#34d399" },
  select: {
    width: "100%", padding: "10px 12px", border: "1px solid #d1d5db",
    borderRadius: 8, fontSize: 13, color: "#111827", background: "#ffffff",
    boxSizing: "border-box", cursor: "pointer",
  },
  fieldGroup: { marginBottom: 16 },
  row:  { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 },
  row3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 },
  btnPrimary:  { padding: "11px 28px", borderRadius: 10, border: "none", background: "#2563eb", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  btnOutline:  { padding: "11px 22px", borderRadius: 10, border: "1px solid #d1d5db", background: "#fff", color: "#374151", fontSize: 14, cursor: "pointer" },
  btnGhost:    { background: "none", border: "none", color: "#9ca3af", fontSize: 13, cursor: "pointer", padding: "4px 8px" },
  badge:       { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 20 },
  tip:         { fontSize: 12, color: "#6b7280", lineHeight: 1.6, padding: "12px 14px", background: "#f9fafb", borderRadius: 8, borderLeft: "3px solid #e5e7eb" },
  error:       { background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#b91c1c", marginBottom: 16 },
};

// ─── Sous-composants ───────────────────────────────────────────────────────────
function Field({ label, hint, children }) {
  return (
    <div style={S.fieldGroup}>
      <label style={S.label}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function InputField({ label, hint, error, value, onChange, type = "text", placeholder, autoFocus }) {
  const style = { ...S.input, ...(error ? S.inputErr : value ? S.inputOk : {}) };
  return (
    <Field label={label} hint={hint}>
      <input
        type={type} value={value} onChange={onChange}
        placeholder={placeholder} autoFocus={autoFocus} style={style}
      />
      {error && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 3 }}>{error}</div>}
    </Field>
  );
}

function ProgressBar({ current }) {
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "0 8px", marginBottom: 32 }}>
      {STEPS.map((s, i) => (
        <div key={s.id} style={{ display: "flex", alignItems: "center", flex: i < STEPS.length - 1 ? 1 : 0 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
            <div style={{
              width: 38, height: 38, borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: current > s.id ? 16 : 14,
              background: current > s.id ? "#2563eb" : current === s.id ? "#2563eb" : "#f3f4f6",
              color: current >= s.id ? "#fff" : "#9ca3af",
              boxShadow: current === s.id ? "0 0 0 4px rgba(37,99,235,0.2)" : "none",
              fontWeight: 700,
              transition: "all 0.3s",
              border: current === s.id ? "2px solid #2563eb" : "2px solid transparent",
            }}>
              {current > s.id ? "✓" : s.icon}
            </div>
            <div style={{ fontSize: 10, fontWeight: current === s.id ? 700 : 400, color: current >= s.id ? "#2563eb" : "#9ca3af", whiteSpace: "nowrap" }}>
              {s.label}
            </div>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{ flex: 1, height: 2, margin: "0 6px", marginBottom: 22, background: current > s.id ? "#2563eb" : "#e5e7eb", transition: "background 0.4s" }} />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Composant principal ───────────────────────────────────────────────────────
export default function OnboardingWizard({ onComplete, activityType }) {
  // Adaptation légère du vocabulaire selon le questionnaire activité/priorités
  // (partie 1 de l'onboarding personnalisé) — reste factuel sur ce qui existe
  // déjà (module Stocks), aucune promesse de fonctionnalité absente.
  const sellsGoods = activityType === "marchandises" || activityType === "mixte";
  const [step, setStep]       = useState(1);
  const [checking, setChecking] = useState(true);

  // Double-vérification au montage : si onboarding déjà terminé côté serveur, fermer immédiatement
  useEffect(() => {
    api.onboarding.status()
      .then(s => {
        if (s?.completed) onComplete();
        else setChecking(false);
      })
      .catch(() => setChecking(false));
  }, []); // eslint-disable-line

  const [companyId, setCompanyId] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState("");
  const [skipped, setSkipped] = useState({ s3: false });

  // Données par étape
  const [s1, setS1] = useState({
    name: "", legal_form: "SAS", siret: "", tva_number: "",
    address: "", postal_code: "", city: "", country: "France",
    phone: "", email: "", website: "",
  });
  const [s2, setS2] = useState({
    currency: "EUR", document_lang: "fr", payment_terms: "30 jours nets",
    default_tva_rate: "20", invoice_prefix: `FACT-${new Date().getFullYear()}-`,
    iban: "", legal_mentions: "",
  });
  const [s3, setS3] = useState({ name: "", email: "", phone: "", city: "", country: "France" });

  const u1 = (k, v) => setS1(x => ({ ...x, [k]: v }));
  const u2 = (k, v) => setS2(x => ({ ...x, [k]: v }));
  const u3 = (k, v) => setS3(x => ({ ...x, [k]: v }));
  if (checking) return null;

  // ── Validations étape 1 ─────────────────────────────────────
  const sirenErr  = s1.siret && !isSirenValid(s1.siret) ? "9 chiffres requis" : "";
  const emailErr1 = s1.email && !isEmailValid(s1.email) ? "Format email invalide" : "";
  const canStep1  = s1.name.trim().length >= 2 && !sirenErr && !emailErr1;

  // ── Validations étape 3 ─────────────────────────────────────
  const emailErr3 = s3.email && !isEmailValid(s3.email) ? "Format email invalide" : "";
  const canStep3  = s3.name.trim().length >= 2 && !emailErr3;

  // ── Sauvegarde étape 1 ──────────────────────────────────────
  const handleStep1 = async () => {
    setSaving(true); setErr("");
    try {
      const { company_id } = await api.onboarding.saveCompany({ ...s1 });
      setCompanyId(company_id);
      setStep(2);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  // ── Sauvegarde étape 2 ──────────────────────────────────────
  const handleStep2 = async () => {
    setSaving(true); setErr("");
    try {
      await api.onboarding.saveBilling({ company_id: companyId, ...s2 });
      setStep(3);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  // ── Sauvegarde étape 3 (ou passer) ─────────────────────────
  const handleStep3 = async (skip = false) => {
    if (skip) { setSkipped(x => ({ ...x, s3: true })); setStep(4); return; }
    setSaving(true); setErr("");
    try {
      await api.onboarding.saveFirstCustomer({ company_id: companyId, ...s3 });
      setStep(4);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  // ── Finalisation ────────────────────────────────────────────
  const handleComplete = async (nav) => {
    setSaving(true); setErr("");
    try {
      await api.onboarding.markComplete();
      await onComplete();
      if (nav) nav();
    } catch (e) { setErr(e.message); setSaving(false); }
  };

  return (
    <div style={S.overlay}>
      <div style={S.card}>

        {/* ── En-tête ──────────────────────────────────────── */}
        <div style={S.header}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 24 }}>💰</span>
              <span style={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>FinanceApp</span>
            </div>
            <span style={{ fontSize: 12, color: "#9ca3af" }}>Étape {step} / {STEPS.length}</span>
          </div>
          <ProgressBar current={step} />
        </div>

        {/* ── Message d'erreur global ───────────────────────── */}
        {err && <div style={{ ...S.error, margin: "0 40px 16px" }}>{err}</div>}

        {/* ══════════════════════════════════════════════════
            ÉTAPE 1 — Bienvenue & Profil société
            ══════════════════════════════════════════════════ */}
        {step === 1 && (
          <div style={S.body}>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: "#111827", margin: "0 0 6px" }}>
                Bienvenue sur FinanceApp 👋
              </h2>
              <p style={{ fontSize: 14, color: "#6b7280", margin: 0, lineHeight: 1.6 }}>
                Configurons votre espace de travail en 5 minutes.
              </p>
            </div>

            <div style={S.row}>
              <InputField label="Nom de la société *" placeholder="Ma Société SAS" value={s1.name} autoFocus
                onChange={e => u1("name", e.target.value)} />
              <Field label="Forme juridique">
                <select style={S.select} value={s1.legal_form} onChange={e => u1("legal_form", e.target.value)}>
                  {LEGAL_FORMS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </Field>
            </div>

            <div style={S.row}>
              <div>
                <label style={S.label}>SIREN (9 chiffres)</label>
                <input style={{ ...S.input, ...(sirenErr ? S.inputErr : s1.siret ? S.inputOk : {}) }}
                  value={s1.siret} placeholder="123 456 789"
                  onChange={e => u1("siret", e.target.value)} />
                {sirenErr && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 3 }}>
                  ✗ {sirenErr}
                </div>}
                {s1.siret && !sirenErr && <div style={{ fontSize: 11, color: "#059669", marginTop: 3 }}>✓ SIREN valide</div>}
              </div>
              <InputField label="N° TVA intracommunautaire" placeholder="FR12 123 456 789"
                value={s1.tva_number} onChange={e => u1("tva_number", e.target.value)} />
            </div>

            <InputField label="Adresse" placeholder="12 rue de la Paix"
              value={s1.address} onChange={e => u1("address", e.target.value)} />

            <div style={S.row3}>
              <InputField label="Code postal" placeholder="75001"
                value={s1.postal_code} onChange={e => u1("postal_code", e.target.value)} />
              <InputField label="Ville" placeholder="Paris"
                value={s1.city} onChange={e => u1("city", e.target.value)} />
              <InputField label="Pays" placeholder="France"
                value={s1.country} onChange={e => u1("country", e.target.value)} />
            </div>

            <div style={S.row}>
              <InputField label="Téléphone" placeholder="+33 1 00 00 00 00"
                type="tel" value={s1.phone} onChange={e => u1("phone", e.target.value)} />
              <div>
                <label style={S.label}>Email de contact</label>
                <input style={{ ...S.input, ...(emailErr1 ? S.inputErr : s1.email ? S.inputOk : {}) }}
                  type="email" value={s1.email} placeholder="contact@masociete.fr"
                  onChange={e => u1("email", e.target.value)} />
                {emailErr1 && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 3 }}>✗ {emailErr1}</div>}
              </div>
            </div>

            <InputField label="Site web" placeholder="https://www.masociete.fr"
              type="url" value={s1.website} onChange={e => u1("website", e.target.value)} />
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            ÉTAPE 2 — Paramètres de facturation
            ══════════════════════════════════════════════════ */}
        {step === 2 && (
          <div style={S.body}>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: "#111827", margin: "0 0 6px" }}>
                Vos préférences de facturation 🧾
              </h2>
              <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
                Ces paramètres seront appliqués automatiquement à toutes vos factures.
                {sellsGoods && " Vous pourrez aussi gérer vos produits et niveaux de stock depuis le module Stocks."}
              </p>
            </div>

            <div style={S.row}>
              <Field label="Devise principale">
                <select style={S.select} value={s2.currency} onChange={e => u2("currency", e.target.value)}>
                  {CURRENCIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </Field>
              <Field label="Langue des documents">
                <select style={S.select} value={s2.document_lang} onChange={e => u2("document_lang", e.target.value)}>
                  {LANGS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </Field>
            </div>

            <div style={S.row}>
              <Field label="Conditions de paiement par défaut">
                <select style={S.select} value={s2.payment_terms} onChange={e => u2("payment_terms", e.target.value)}>
                  {PAYMENT_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <InputField label="Taux de TVA par défaut (%)" placeholder="20"
                type="number" value={s2.default_tva_rate}
                onChange={e => u2("default_tva_rate", e.target.value)} />
            </div>

            <InputField label="Préfixe de numérotation des factures"
              placeholder={`FACT-${new Date().getFullYear()}-`}
              value={s2.invoice_prefix}
              onChange={e => u2("invoice_prefix", e.target.value)}
              hint={`Exemple : ${s2.invoice_prefix || "FACT-2026-"}0001`} />

            <div style={S.fieldGroup}>
              <label style={S.label}>
                IBAN bancaire (optionnel)
                <span style={{ ...S.badge, background: "#f0fdf4", color: "#059669", marginLeft: 8 }}>
                  🔐 AES-256-GCM
                </span>
              </label>
              <input
                style={{ ...S.input, ...(s2.iban && !isIbanValid(s2.iban) ? S.inputErr : s2.iban ? S.inputOk : {}) }}
                value={s2.iban} placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                onChange={e => u2("iban", e.target.value.toUpperCase())}
              />
              {s2.iban && !isIbanValid(s2.iban) && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 3 }}>✗ Format IBAN invalide</div>}
              {s2.iban && isIbanValid(s2.iban) && <div style={{ fontSize: 11, color: "#059669", marginTop: 3 }}>✓ IBAN valide — chiffrement AES-256-GCM</div>}
            </div>

            <Field label="Mentions légales personnalisées (optionnel)">
              <textarea
                rows={3} value={s2.legal_mentions}
                placeholder="Ex : Pénalités de retard : taux BCE + 10 points. Indemnité forfaitaire de 40€ pour frais de recouvrement."
                onChange={e => u2("legal_mentions", e.target.value)}
                style={{ ...S.input, resize: "vertical" }}
              />
            </Field>
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            ÉTAPE 3 — Premier client
            ══════════════════════════════════════════════════ */}
        {step === 3 && (
          <div style={S.body}>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: "#111827", margin: "0 0 6px" }}>
                Ajoutez votre premier client 👥
                <span style={{ ...S.badge, background: "#f3f4f6", color: "#6b7280", marginLeft: 10, verticalAlign: "middle" }}>
                  Optionnel
                </span>
              </h2>
              <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
                Vous pourrez en ajouter d'autres — ou celui-ci plus tard — depuis le module Clients. Pas de client sous la main ? Passez directement à l'étape suivante.
              </p>
            </div>

            <InputField label="Nom de la société ou du particulier *" placeholder="Dupont & Associés SAS" autoFocus
              value={s3.name} onChange={e => u3("name", e.target.value)} />

            <div style={S.row}>
              <div>
                <label style={S.label}>Email</label>
                <input style={{ ...S.input, ...(emailErr3 ? S.inputErr : s3.email ? S.inputOk : {}) }}
                  type="email" value={s3.email} placeholder="contact@dupont.fr"
                  onChange={e => u3("email", e.target.value)} />
                {emailErr3 && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 3 }}>✗ {emailErr3}</div>}
              </div>
              <InputField label="Téléphone" type="tel" placeholder="+33 1 00 00 00 00"
                value={s3.phone} onChange={e => u3("phone", e.target.value)} />
            </div>

            <div style={S.row}>
              <InputField label="Ville" placeholder="Lyon"
                value={s3.city} onChange={e => u3("city", e.target.value)} />
              <InputField label="Pays" placeholder="France"
                value={s3.country} onChange={e => u3("country", e.target.value)} />
            </div>

            <div style={S.tip}>
              💡 Ce client sera immédiatement disponible dans votre module Facturation pour créer votre première facture.
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            ÉTAPE 4 — Récapitulatif & Lancement
            ══════════════════════════════════════════════════ */}
        {step === 4 && (
          <div style={S.body}>
            <div style={{ marginBottom: 24, textAlign: "center" }}>
              <div style={{ fontSize: 52, marginBottom: 10 }}>🎉</div>
              <h2 style={{ fontSize: 24, fontWeight: 800, color: "#111827", margin: "0 0 8px" }}>
                Tout est prêt !
              </h2>
              <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
                Votre espace FinanceApp est configuré. Voici un récapitulatif.
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
              {[
                { icon: "🏢", label: "Société configurée", value: s1.name + (s1.legal_form ? ` — ${s1.legal_form}` : ""), done: true },
                { icon: "🧾", label: "Facturation paramétrée", value: `${s2.currency} · TVA ${s2.default_tva_rate}% · Préfixe ${s2.invoice_prefix}`, done: true },
                { icon: "👥", label: "Premier client", value: skipped.s3 ? null : (s3.name || null), done: !skipped.s3 },
              ].map(item => (
                <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", background: "#f9fafb", borderRadius: 10 }}>
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{item.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{item.label}</div>
                    {item.value && <div style={{ fontSize: 12, color: "#6b7280", marginTop: 1 }}>{item.value}</div>}
                  </div>
                  <span style={{
                    fontSize: 16, flexShrink: 0,
                    color: item.done ? "#059669" : "#9ca3af",
                  }}>
                    {item.done ? "✅" : "⏭"}
                  </span>
                </div>
              ))}
            </div>

            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 10 }}>
                Actions rapides suggérées :
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[
                  { icon: "📄", label: "Créer ma première facture", page: "facturation" },
                  { icon: "📊", label: "Voir le tableau de bord", page: "dashboard" },
                  { icon: "📥", label: "Importer mes clients", page: "clients" },
                ].map(action => (
                  <button
                    key={action.label}
                    onClick={() => handleComplete(() => window.dispatchEvent(new CustomEvent('navigate', { detail: action.page })))}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 16px", background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 10, cursor: "pointer", fontSize: 13, color: "#374151", fontWeight: 500, textAlign: "left", width: "100%" }}
                  >
                    <span style={{ fontSize: 18 }}>{action.icon}</span>
                    {action.label}
                    <span style={{ marginLeft: "auto", color: "#9ca3af" }}>→</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Navigation ────────────────────────────────────── */}
        <div style={S.footer}>
          {/* Bouton Précédent */}
          {step > 1 && step < 4 ? (
            <button onClick={() => setStep(s => s - 1)} style={S.btnOutline}>← Précédent</button>
          ) : <div />}

          {/* Centre — bouton Passer */}
          {step === 3 && (
            <button onClick={() => handleStep3(true)} style={S.btnGhost}>
              Passer cette étape →
            </button>
          )}
          {(step === 1 || step === 2) && <div />}

          {/* Bouton Suivant / Commencer */}
          {step === 1 && (
            <button onClick={handleStep1} disabled={!canStep1 || saving}
              style={{ ...S.btnPrimary, opacity: !canStep1 || saving ? 0.5 : 1, cursor: !canStep1 || saving ? "not-allowed" : "pointer" }}>
              {saving ? "Enregistrement…" : "Suivant →"}
            </button>
          )}
          {step === 2 && (
            <button onClick={handleStep2} disabled={saving}
              style={{ ...S.btnPrimary, opacity: saving ? 0.5 : 1, cursor: saving ? "not-allowed" : "pointer" }}>
              {saving ? "Enregistrement…" : "Suivant →"}
            </button>
          )}
          {step === 3 && (
            <button onClick={() => handleStep3(false)} disabled={!canStep3 || saving}
              style={{ ...S.btnPrimary, opacity: !canStep3 || saving ? 0.5 : 1, cursor: !canStep3 || saving ? "not-allowed" : "pointer" }}>
              {saving ? "Enregistrement…" : "Suivant →"}
            </button>
          )}
          {step === 4 && (
            <button onClick={() => handleComplete(null)} disabled={saving}
              style={{ ...S.btnPrimary, padding: "13px 36px", fontSize: 15, opacity: saving ? 0.5 : 1, cursor: saving ? "not-allowed" : "pointer" }}>
              {saving ? "Configuration en cours…" : "🚀 Commencer à utiliser FinanceApp"}
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
